import {
  Icon,
  SidebarMenuButton,
  SidebarMenuItem,
  Text,
} from "@salestrak/ui";
import React from "react";

import { SidebarSection } from "@salestrak/ui";
import { useSidebarNav } from "../use-sidebar-nav";
import { usePermission } from "#app/lib/hooks/use-permission";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { useCloseSidebarIfMobile } from "#app/lib/mobile-sidebar-context";
import { useNavDialogActions } from "#app/lib/nav-dialogs-context";
import { EntityTag } from "#app/components/entity";

/**
 * "My Teams" module-menu section — every team the viewer can access (all
 * accessible teams, not membership-only — the title is the shorthand, not a
 * membership filter). The `useWorkspaceData().teams` set is already
 * visibility-scoped by the API. Rows navigate to the team's dashboard
 * (`/team/[teamId]`); the create affordances (header "+" and the "Create Team"
 * row) open CreateTeamDialog, gated on the `manage.teams` permission (the same
 * key the createTeam mutation enforces server-side).
 */
export function MyTeamsSection() {
  const { navigate, isActive } = useSidebarNav();
  const { teams } = useWorkspaceData();
  const canCreateTeam = usePermission("manage.teams");
  const closeSidebar = useCloseSidebarIfMobile();
  const nav = useNavDialogActions();

  const openCreateTeam = () => {
    closeSidebar();
    nav.openCreateTeam();
  };

  // With no teams AND no create permission there's nothing to show or do —
  // hide the whole section rather than render an empty, actionless accordion.
  // (No teams + can create → keep it for the CTA.)
  if (teams.length === 0 && !canCreateTeam) return null;

  return (
    <>
      <SidebarSection
        // Header "+" — same flow as the Create team row below; omitted
        // entirely without create permission.
        action={
          canCreateTeam
            ? { icon: "plus", label: "Create Team", onClick: openCreateTeam }
            : undefined
        }
        title="My Teams"
      >
        {teams.map((team, index) => (
          <SidebarMenuItem key={`${team.teamId}-${index}`}>
            <SidebarMenuButton
              isActive={isActive(`/team/${team.teamId}`)}
              onClick={() => navigate(`/team/${team.teamId}`)}
            >
              <EntityTag
                entity={{ id: team.teamId, kind: "team", team }}
                interactive={false}
                weight="regular"
              />
            </SidebarMenuButton>
          </SidebarMenuItem>
        ))}
        {canCreateTeam && (
          <SidebarMenuItem>
            <SidebarMenuButton onClick={openCreateTeam}>
              <Icon color="faint" name="plus" />
              <Text truncate variant="faint">
                Create Team
              </Text>
            </SidebarMenuButton>
          </SidebarMenuItem>
        )}
      </SidebarSection>
    </>
  );
}
